import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Pencil, Plus, Building2, Globe2, Loader2, Trash2, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlant } from "@/context/PlantContext";
import { PageHeader, Panel, StatusChip } from "@/components/common";

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

function PlantDialog({ plant, entities, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (plant) setF({ name: plant.name || "", country: plant.country || "", entity_id: plant.entity_id || "none", is_eu: plant.is_eu == null ? "unset" : plant.is_eu ? "eu" : "non_eu", dds_filing_entity_id: plant.dds_filing_entity_id || "none" }); }, [plant]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const onEntity = (v) => {
    const e = entities.find((x) => x.id === v);
    setF((x) => ({ ...x, entity_id: v, ...(e ? { is_eu: e.is_eu ? "eu" : "non_eu", country: x.country || e.country, dds_filing_entity_id: x.dds_filing_entity_id === "none" ? v : x.dds_filing_entity_id } : {}) }));
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/plants/${plant.code}`, {
        name: f.name, country: f.country.toUpperCase(), entity_id: f.entity_id === "none" ? null : f.entity_id,
        is_eu: f.is_eu === "unset" ? null : f.is_eu === "eu", dds_filing_entity_id: f.dds_filing_entity_id === "none" ? null : f.dds_filing_entity_id,
      });
      toast.success(`Plant ${plant.code} saved`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!plant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="plant-dialog">
        <DialogHeader><DialogTitle>Plant {plant?.code}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label="Name"><Input data-testid="plant-name-input" value={f.name || ""} onChange={set("name")} /></Field></div>
          <Field label="Country (ISO)"><Input data-testid="plant-country-input" maxLength={2} className="uppercase" value={f.country || ""} onChange={set("country")} placeholder="GB" /></Field>
          <Field label="EU / non-EU">
            <Select value={f.is_eu || "unset"} onValueChange={set("is_eu")}>
              <SelectTrigger data-testid="plant-eu-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">Not set</SelectItem>
                <SelectItem value="eu" data-testid="plant-eu-option-eu">EU</SelectItem>
                <SelectItem value="non_eu" data-testid="plant-eu-option-non_eu">Non-EU</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="col-span-2">
            <Field label="Legal entity">
              <Select value={f.entity_id || "none"} onValueChange={onEntity}>
                <SelectTrigger data-testid="plant-entity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not assigned</SelectItem>
                  {entities.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`plant-entity-option-${e.code}`}>{e.name} ({e.country})</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="DDS filing entity">
              <Select value={f.dds_filing_entity_id || "none"} onValueChange={set("dds_filing_entity_id")}>
                <SelectTrigger data-testid="plant-filing-entity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not assigned</SelectItem>
                  {entities.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`plant-filing-option-${e.code}`}>{e.name} ({e.country})</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <p className="mt-1.5 text-xs text-slate-500">Entity that files the due diligence statement in TRACES for goods placed on / exported from the EU market by this plant.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="plant-save-btn" onClick={save} disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save plant</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_E = { name: "", code: "", country: "", is_eu: false, eori: "", xi_eori: "", eudr_operator_id: "", address: "" };

const gbp = (v, conv) => `£${fmtNum(Math.round(v || 0))}${conv ? " (converted)" : ""}`;

function InStock({ p }) {
  const s = p.in_stock || {};
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`plant-in-stock-${p.code}`}>
      <Link to={`/materials?plant=${p.code}&scope=in_scope&stock=in`} data-testid={`plant-in-stock-link-${p.code}`} className="font-medium text-emerald-700 underline-offset-2 hover:underline"
        title={`In scope with stock > 0 · total qty ${fmtNum(s.qty)} · stock value ${gbp(s.value_gbp, s.converted)}`}>{fmtNum(s.in_scope)}</Link>
      {s.check > 0 && <Link to={`/materials?plant=${p.code}&scope=check&stock=in`} data-testid={`plant-in-stock-check-${p.code}`} className="text-[11px] text-amber-700 hover:underline"
        title={`Check with stock > 0 · total qty ${fmtNum(s.check_qty)} · stock value ${gbp(s.check_value_gbp, s.converted)}`}>+{fmtNum(s.check)} Check</Link>}
    </span>
  );
}

function DeletePlantDialog({ code, onClose, onDone }) {
  const [pv, setPv] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPv(null); setTyped(""); if (code) api.get(`/plants/${code}/delete-preview`).then((r) => setPv(r.data)).catch((e) => toast.error(errMsg(e))); }, [code]);
  const run = async (kind) => {
    setBusy(true);
    try {
      if (kind === "delete") await api.post(`/plants/${code}/delete`, { confirm: typed });
      else await api.post(`/plants/${code}/archive`);
      toast.success(kind === "delete" ? `Plant ${code} deleted` : `Plant ${code} archived`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="plant-delete-dialog">
        <DialogHeader><DialogTitle>Delete plant {code}</DialogTitle></DialogHeader>
        {!pv ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : pv.blocked ? (
          <div className="space-y-3 text-sm" data-testid="plant-delete-blocked">
            <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 font-medium text-rose-800">{pv.message}</p>
            <ul className="ml-4 list-disc text-slate-700">{pv.links.map((l) => <li key={l.kind} data-testid={`plant-delete-link-${l.kind}`}>{fmtNum(l.count)} {l.kind}{l.examples?.length ? ` (${l.examples.slice(0, 5).join(", ")}${l.count > 5 ? "…" : ""})` : ""}</li>)}</ul>
            <p className="text-slate-500">Archive hides the plant everywhere and keeps all its data. You can unarchive it later.</p>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">These records will be removed. Vendors, customers and materials used at other plants stay.</p>
            <table className="w-full text-sm" data-testid="plant-delete-counts"><tbody>{pv.counts.map((c) => <tr key={c.collection} className="border-b border-slate-100"><td className="py-1">{c.label}</td><td className="py-1 text-right font-mono" data-testid={`plant-delete-count-${c.collection}`}>{fmtNum(c.count)}</td></tr>)}</tbody></table>
            <Field label={`Type ${code} to confirm`}><Input data-testid="plant-delete-confirm-input" className="font-mono" value={typed} onChange={(e) => setTyped(e.target.value)} /></Field>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {pv && <Button variant="outline" data-testid="plant-archive-btn" onClick={() => run("archive")} disabled={busy}><Archive className="mr-2 h-4 w-4" />Archive plant</Button>}
          {pv && !pv.blocked && <Button data-testid="plant-delete-confirm-btn" className="bg-rose-600 hover:bg-rose-700" onClick={() => run("delete")} disabled={busy || typed.trim() !== code}><Trash2 className="mr-2 h-4 w-4" />Delete plant</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchivedPlants({ reloadKey, onChanged }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/plants", { params: { archived: true } }).then((r) => setList(r.data)); }, [reloadKey]);
  if (!list.length) return null;
  const un = async (code) => { try { await api.post(`/plants/${code}/unarchive`); toast.success(`Plant ${code} unarchived`); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title={`Archived plants (${list.length})`} testid="archived-plants-panel">
      {list.map((p) => <div key={p.code} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm" data-testid={`archived-plant-${p.code}`}>
        <span><span className="font-mono font-semibold">{p.code}</span> {p.name} <span className="text-xs text-slate-500">· data kept · archived {p.archived_by ? `by ${p.archived_by}` : ""}</span></span>
        <Button size="sm" variant="outline" data-testid={`plant-unarchive-${p.code}`} onClick={() => un(p.code)}><ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />Unarchive</Button>
      </div>)}
    </Panel>
  );
}

function EntityDialog({ entity, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY_E);
  useEffect(() => { if (entity) setF({ ...EMPTY_E, ...entity }); }, [entity]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    try {
      const body = { name: f.name, code: f.code, country: f.country.toUpperCase(), is_eu: f.is_eu, eori: f.eori, xi_eori: f.xi_eori || "", eudr_operator_id: f.eudr_operator_id, address: f.address };
      if (entity.id) await api.put(`/entities/${entity.id}`, body);
      else await api.post("/entities", body);
      toast.success("Legal entity saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!entity} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="entity-dialog">
        <DialogHeader><DialogTitle>{entity?.id ? `Edit ${entity.name}` : "New legal entity"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input data-testid="entity-name-input" value={f.name} onChange={set("name")} /></Field>
          <Field label="Code"><Input data-testid="entity-code-input" value={f.code} onChange={set("code")} /></Field>
          <Field label="Country (ISO)"><Input data-testid="entity-country-input" maxLength={2} className="uppercase" value={f.country} onChange={set("country")} /></Field>
          <Field label="EU member state"><label className="flex h-10 items-center gap-2 text-sm"><Switch checked={f.is_eu} onCheckedChange={(v) => setF((x) => ({ ...x, is_eu: v }))} data-testid="entity-eu-switch" /> {f.is_eu ? "EU" : "Non-EU"}</label></Field>
          <Field label="EORI"><Input data-testid="entity-eori-input" className="font-mono" value={f.eori} onChange={set("eori")} placeholder="GB123456789000" /></Field>
          <Field label="EUDR operator ID"><Input data-testid="entity-operator-input" className="font-mono" value={f.eudr_operator_id} onChange={set("eudr_operator_id")} /></Field>
          <div className="col-span-2"><Field label="XI EORI (Northern Ireland)"><Input data-testid="entity-xi-eori-input" className="font-mono" value={f.xi_eori || ""} onChange={set("xi_eori")} placeholder="XI123456789000" /></Field>
            <p className="mt-1 text-xs text-slate-500">Used as the operator EORI on GB→NI drafts (EUDR applies in Northern Ireland from 30 Dec 2026).</p></div>
          <div className="col-span-2"><Field label="Address"><Input value={f.address} onChange={set("address")} /></Field></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="entity-save-btn" onClick={save}>Save entity</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Plants() {
  const { can } = useAuth();
  const { plants, entities, reload } = usePlant();
  const [edit, setEdit] = useState(null);
  const [editE, setEditE] = useState(null);
  const [del, setDel] = useState(null);
  const [arch, setArch] = useState(0);
  const refresh = () => { reload(); setArch((x) => x + 1); };
  const [sp, setSp] = useSearchParams();
  const name = (id) => entities.find((e) => e.id === id)?.name;
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const code = sp.get("edit");
    const p = code && can.admin && plants.find((x) => x.code === code);
    if (p) { setEdit(p); setSp({}, { replace: true }); }
  }, [sp, plants, can.admin]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="plants-page">
      <PageHeader title="Plants & legal entities" subtitle="Assign each SAP plant to a legal entity, mark EU / non-EU and choose who files the DDS. Plants found in uploads are auto-created as “Needs setup”." />
      <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {entities.map((e) => (
          <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`entity-card-${e.code}`}>
            <div className="flex items-start justify-between">
              <span className="rounded-lg bg-[#1C1C1E] p-2 text-white"><Globe2 className="h-4 w-4" /></span>
              {can.admin && <button data-testid={`entity-edit-${e.code}`} onClick={() => setEditE(e)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>}
            </div>
            <h3 className="mt-3 font-semibold text-slate-900">{e.name}</h3>
            <p className="text-xs text-slate-500">{e.country} · {e.is_eu ? "EU" : "Non-EU"} · {plants.filter((p) => p.entity_id === e.id).length} plants</p>
            <p className="mt-2 font-mono text-xs text-slate-500">EORI {e.eori || "—"} · Operator {e.eudr_operator_id || "—"}</p>
            {e.xi_eori && <p className="font-mono text-xs text-slate-500" data-testid={`entity-xi-eori-${e.code}`}>XI EORI {e.xi_eori}</p>}
          </div>
        ))}
        {can.admin && (
          <button data-testid="entity-add-btn" onClick={() => setEditE({})} className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 p-5 text-sm font-medium text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700">
            <Plus className="h-4 w-4" /> Add legal entity
          </button>
        )}
      </div>
      <Panel title={`Plants (${plants.length})`} testid="plants-table-panel">
        <div className="-m-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="plants-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>{["Plant", "Name", "Country", "Legal entity", "EU / non-EU", "DDS filing entity", "Materials", "In scope", "In scope in stock", "Setup", ""].map((h) => <th key={h} className="px-5 py-2.5 text-left">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {plants.map((p) => (
                <tr key={p.code} data-testid={`plant-row-${p.code}`} className="hover:bg-slate-50/60">
                  <td className="px-5 py-3 font-mono font-semibold text-slate-900"><Building2 className="mr-2 inline h-4 w-4 text-slate-400" />{p.code}</td>
                  <td className="px-5 py-3">{p.name}</td>
                  <td className="px-5 py-3" data-testid={`plant-country-${p.code}`}>{p.country || "—"}</td>
                  <td className="px-5 py-3" data-testid={`plant-entity-${p.code}`}>{name(p.entity_id) || "—"}</td>
                  <td className="px-5 py-3" data-testid={`plant-eu-${p.code}`}>{p.is_eu == null ? "—" : p.is_eu ? "EU" : "Non-EU"}</td>
                  <td className="px-5 py-3" data-testid={`plant-filing-${p.code}`}>{name(p.dds_filing_entity_id) || "—"}</td>
                  <td className="px-5 py-3">{fmtNum(p.materials)}</td>
                  <td className="px-5 py-3 font-medium text-emerald-700">{fmtNum(p.in_scope)}</td>
                  <td className="px-5 py-3"><InStock p={p} /></td>
                  <td className="px-5 py-3" data-testid={`plant-status-${p.code}`}>{p.needs_setup ? <StatusChip status="setup" label="Needs setup" /> : <StatusChip status="completed" label="Ready" />}</td>
                  <td className="whitespace-nowrap px-5 py-3 text-right">{can.admin && <>
                    <Button size="sm" variant="ghost" data-testid={`plant-edit-${p.code}`} onClick={() => setEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" data-testid={`plant-delete-${p.code}`} title="Delete or archive plant" onClick={() => setDel(p.code)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                  </>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <PlantDialog plant={edit} entities={entities} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />
      <EntityDialog entity={editE} onClose={() => setEditE(null)} onSaved={() => { setEditE(null); reload(); }} />
      {can.admin && <div className="mt-6"><ArchivedPlants reloadKey={arch} onChanged={refresh} /></div>}
      <DeletePlantDialog code={del} onClose={() => setDel(null)} onDone={() => { setDel(null); refresh(); }} />
    </div>
  );
}
